import type { CastAssignment, Personnel } from '../../personnel/types';
import {
  expandScheduleEventAttendeeIds,
  type ScheduleEventAttendeeExpansionContext,
} from '../../scheduler/attendees';
import { getResolvedScheduleCallAttendeeIds } from '../../scheduler/call-resolution';
import type { RehearsalScene, ScheduleEvent } from '../../scheduler/types';
import type { ShowStructure } from '../../show-structure/types';
import type { CommunicationLinkedSource } from './types';

export interface DeriveCalledRecipientIdsForEventInput {
  event: ScheduleEvent;
  personnel: Personnel[];
  showStructure: Pick<ShowStructure, 'characters' | 'acts' | 'scenes'>;
  rehearsalScenes: RehearsalScene[];
  castAssignments: CastAssignment[];
}

export function scheduleEventLinkedSource(eventId: string): CommunicationLinkedSource {
  return { type: 'schedule-event', id: eventId };
}

export function deadlineLinkedSource(deadlineId: string): CommunicationLinkedSource {
  return { type: 'deadline', id: deadlineId };
}

export function manualLinkedSource(): CommunicationLinkedSource {
  return { type: 'manual' };
}

function addDeduped(contactIds: string[], seen: Set<string>, contactId: string): void {
  if (seen.has(contactId)) {
    return;
  }

  seen.add(contactId);
  contactIds.push(contactId);
}

export function expandAudienceContactIds(
  attendees: string[],
  personnel: Personnel[],
  context?: ScheduleEventAttendeeExpansionContext
): string[] {
  return expandScheduleEventAttendeeIds(attendees, personnel, context);
}

export function deriveCalledRecipientIdsForEvent(
  input: DeriveCalledRecipientIdsForEventInput
): string[] {
  const { event, personnel, showStructure, rehearsalScenes, castAssignments } = input;
  return getResolvedScheduleCallAttendeeIds(event, {
    acts: showStructure.acts,
    scenes: showStructure.scenes,
    characters: showStructure.characters,
    rehearsalScenes,
    castAssignments,
    personnel,
  });
}

export function deriveDeadlineRecipientIdsForEvent(
  event: ScheduleEvent,
  personnel: Personnel[]
): string[] {
  if (event.type !== 'deadline') {
    return [];
  }

  const contactIds: string[] = [];
  const seen = new Set<string>();
  const addContact = (contactId: string) => addDeduped(contactIds, seen, contactId);

  if (event.assignedTo) {
    addContact(event.assignedTo);
  }

  if (event.linkedDepartment && event.linkedDepartment !== 'general') {
    for (const contact of personnel) {
      if (!contact.deleted && contact.department === event.linkedDepartment) {
        addContact(contact.id);
      }
    }
  }

  for (const contactId of expandAudienceContactIds(event.attendees ?? [], personnel)) {
    addContact(contactId);
  }

  return contactIds;
}
